import { BadgeCheck } from "lucide-react";
import { format } from "date-fns";
import { et } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

interface VerifiedBadgeProps {
  /**
   * When this document version was verified, or `null`/undefined when it isn't.
   * This is the boolean — `public.documents` has no separate flag column.
   */
  verifiedAt?: string | null;
  /** Display name of the verifier, when it could be resolved. */
  verifiedByName?: string | null;
  /**
   * Drop the "Verified" word below a narrow container, leaving just the icon —
   * the same rule (and the same `@[500px]` breakpoint) the content-match chip
   * uses, so a long filename squeezes both marks identically. Requires an
   * `@container` ancestor; set it only where one exists.
   */
  collapsible?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * VerifiedBadge - Atom component
 *
 * The "Verified" mark shown next to a document's filename on every documents
 * tab (Timeline, List, Folders). Hovering reveals who verified it and when —
 * both values are stamped server-side by the `trg_stamp_documents_verified`
 * trigger, so the attribution shown here is the DB's, never the client's.
 *
 * Renders nothing for an unverified document, so callers can drop it in
 * unconditionally.
 *
 * Deliberately not routed through `DocumentTags`: that component measures its
 * children and collapses the overflow into "+N more", and a status mark must
 * never end up hidden behind an overflow badge.
 */
export function VerifiedBadge({
  verifiedAt,
  verifiedByName,
  collapsible = false,
  className,
}: VerifiedBadgeProps) {
  const { t, language } = useLanguage();

  if (!verifiedAt) return null;

  const copy = t.documents.verified;
  const parsed = new Date(verifiedAt);
  // A malformed timestamp must not take the row down with it — fall back to the
  // raw value rather than rendering "Invalid Date".
  const when = Number.isNaN(parsed.getTime())
    ? verifiedAt
    : format(parsed, "d MMM yyyy HH:mm", { locale: language === "et" ? et : undefined });

  const tooltip = verifiedByName
    ? copy.tooltip.replace("{name}", verifiedByName).replace("{date}", when)
    : copy.tooltipUnknownUser.replace("{date}", when);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={cn(
              "h-5 shrink-0 cursor-default gap-1 border-success/40 bg-success/10 text-success",
              collapsible ? "px-1 @[500px]:px-1.5" : "px-1.5",
              className
            )}
            // The tooltip is hover/focus-only, so the accessible name has to
            // carry the same attribution for screen readers.
            aria-label={tooltip}
          >
            <BadgeCheck className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span
              className={cn(
                "text-[10px] font-medium leading-none",
                collapsible && "hidden @[500px]:inline"
              )}
            >
              {copy.label}
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
